import assert from 'assert';

import BenchlingConfig from 'BenchlingConfig';
import {DocumentUpdateSpec} from 'constants/EventConstants';
import DocumentActions from 'notebook/documents/DocumentActions';
import {getNoteFromDocument} from 'notebook/documents/DocumentSelectors';
import documentStore from 'notebook/documents/DocumentStore';
import DocumentUtil from 'notebook/documents/DocumentUtil';
import {createRenameDocumentTokenUpdater} from 'notebook/tables/excess/ops/TokenUpdater';
import {performFormulaShifting} from 'notebook/tables/spreadsheets/SpreadsheetEvalUtil';
import {updateNoteWithTableMap} from 'notebook/tables/store/actions/TableActions';
import {getTableMapFromNote} from 'notebook/tables/store/selectors/TableSelectors';

/**
 * This will rename the given document and perform formula shifting across that document and all related
 * documents.
 * @param documentId
 * @param previousDocumentName
 * @param newDocumentName
 * @returns the ids of the documents that were updated
 */
export function performDocumentRename(
  documentId: string,
  previousDocumentName: string,
  newDocumentName: string
): Array<string> {
  const currentDocument = documentStore.getDocument(documentId);
  assert(currentDocument != null, `Cannot find document with id ${documentId}`);

  const relatedDocumentIds = BenchlingConfig.ELN_ENABLE_CROSS_STEP_DATA_REFS
    ? documentStore.getRelatedDocumentIds(documentId)
    : [];
  const relatedDocuments = relatedDocumentIds.map((documentId) => {
    const document = documentStore.getDocument(documentId);
    assert(document != null, `Cannot find related document with id ${documentId}`);
    return {
      documentId,
      documentName: document.name,
      tableMap: getTableMapFromNote(getNoteFromDocument(document)),
    };
  });

  const tableMap = getTableMapFromNote(getNoteFromDocument(currentDocument));

  const tokenUpdater = createRenameDocumentTokenUpdater(previousDocumentName, newDocumentName);

  const {tables: newTableMap, relatedDocuments: updatedRelatedDocuments} = performFormulaShifting({
    tableMap,
    tokenUpdaters: [tokenUpdater],
    documentName: currentDocument.name,
    documentId: currentDocument.id,
    dim: null,
    relatedDocuments,
  });

  const relatedDocumentSpecs: Array<DocumentUpdateSpec> = [];
  for (const relatedDocument of updatedRelatedDocuments) {
    const updatedNote = updateNoteWithTableMap(
      relatedDocument.tableMap,
      getNoteFromDocument(documentStore.getDocument(relatedDocument.documentId)!)
    );
    relatedDocumentSpecs.push({
      id: relatedDocument.documentId,
      spec: DocumentUtil.mergeEntryVersionUpdateWithNote(DocumentUtil.toEntryNote(updatedNote)),
    });
  }

  const updatedNote = updateNoteWithTableMap(newTableMap, getNoteFromDocument(currentDocument));

  DocumentActions.updateDocumentGroup(
    {
      id: currentDocument.id,
      spec: {
        name: {$set: newDocumentName},
        ...DocumentUtil.mergeEntryVersionUpdateWithNote(DocumentUtil.toEntryNote(updatedNote)),
      },
    },
    relatedDocumentSpecs
  );

  return [
    currentDocument.id,
    ...updatedRelatedDocuments.map((relatedDocument) => relatedDocument.documentId),
  ];
}
